import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { User, Save, Loader2, Lock, Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuthorization } from "@/contexts/AuthorizationContext";
import { useAuth } from "@/hooks/useAuth";
import { buscarMeuPerfil, atualizarMeuPerfil } from "@/services/perfilService";
import type { MeuPerfilCompleto } from "@/services/perfilService";
import { useEstados, useCidades } from "@/hooks/useParceiros";
import type { UserRole } from "@/types/permissions";
import { supabase } from "@/integrations/supabase/client";

const formatTelefone = (value: string) => {
  const v = value.replace(/\D/g, "").slice(0, 11);
  if (v.length === 11) return v.replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
  if (v.length === 10) return v.replace(/^(\d{2})(\d{4})(\d{4})$/, "($1) $2-$3");
  return v;
};

const formatCPF = (value: string) => {
  const v = value.replace(/\D/g, "").slice(0, 11);
  return v.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
};

const ROLE_LABELS: Record<UserRole, string> = {
  admin: "Administrador",
  representante: "Representante",
  vendedor: "Vendedor",
  parceiro: "Parceiro",
  financeiro: "Financeiro",
  ai_agent: "Agente IA",
  user: "Usuário",
};

function ValidacaoItem({ valido, texto }: { valido: boolean; texto: string }) {
  return (
    <div className="flex items-center gap-2">
      {valido ? (
        <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
      ) : (
        <XCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
      <span className={`text-sm ${valido ? "font-medium text-green-700" : "text-muted-foreground"}`}>
        {texto}
      </span>
    </div>
  );
}

export function EditarMeuPerfilForm() {
  const { user } = useAuth();
  const { userProfile, refreshUserProfile } = useAuthorization();
  const [perfil, setPerfil] = useState<MeuPerfilCompleto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    nome: "",
    telefone: "",
    cidade: "",
    estado: "",
    cpf: "",
    pix: "",
  });
  const [senhaNova, setSenhaNova] = useState("");
  const [senhaConfirmar, setSenhaConfirmar] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmar, setMostrarConfirmar] = useState(false);
  const [salvandoSenha, setSalvandoSenha] = useState(false);

  const { estados, loading: estadosLoading } = useEstados();

  const mostrarPix = perfil?.role === "parceiro" || perfil?.role === "vendedor";

  const validacoesSenha = {
    tamanho: senhaNova.length >= 6,
    temLetra: /[A-Za-z]/.test(senhaNova),
    temNumero: /\d/.test(senhaNova),
    senhasIguais: senhaNova !== "" && senhaNova === senhaConfirmar,
  };
  const senhaValida =
    validacoesSenha.tamanho &&
    validacoesSenha.temLetra &&
    validacoesSenha.temNumero &&
    validacoesSenha.senhasIguais;
  const { cidades, loading: cidadesLoading } = useCidades(form.estado);

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const p = await buscarMeuPerfil(user.id);
        setPerfil(p);
        if (p) {
          setForm({
            nome: p.nome ?? "",
            telefone: p.telefone ?? "",
            cidade: p.cidade ?? "",
            estado: p.estado ?? "",
            cpf: p.cpf ?? "",
            pix: p.pix ?? "",
          });
        }
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Erro ao carregar perfil");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !perfil) return;

    if (!form.nome.trim()) {
      toast.error("Nome é obrigatório.");
      return;
    }
    const telefoneDigits = form.telefone.replace(/\D/g, "");
    if (telefoneDigits.length < 10) {
      toast.error("Telefone é obrigatório (mínimo 10 dígitos).");
      return;
    }
    if (!form.estado) {
      toast.error("Estado é obrigatório.");
      return;
    }
    if (!form.cidade) {
      toast.error("Cidade é obrigatória.");
      return;
    }

    setSaving(true);
    try {
      const cpfDigits = form.cpf.replace(/\D/g, "");
      await atualizarMeuPerfil(user.id, {
        nome: form.nome.trim(),
        telefone: telefoneDigits || null,
        cidade: form.cidade || null,
        estado: form.estado || null,
        cpf: cpfDigits.length === 11 ? cpfDigits : null,
        pix: mostrarPix ? (form.pix?.trim() || null) : undefined,
      });
      toast.success("Perfil atualizado com sucesso!");
      await refreshUserProfile();
      const p = await buscarMeuPerfil(user.id);
      setPerfil(p);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao atualizar perfil");
    } finally {
      setSaving(false);
    }
  };

  const handleAlterarSenha = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!senhaValida) {
      toast.error("Preencha os requisitos de senha.");
      return;
    }
    setSalvandoSenha(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: senhaNova });
      if (error) throw error;
      toast.success("Senha alterada com sucesso!");
      setSenhaNova("");
      setSenhaConfirmar("");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao alterar senha");
    } finally {
      setSalvandoSenha(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!perfil) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Não foi possível carregar seu perfil.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Meu Perfil
        </CardTitle>
        <CardDescription>
          Edite seus dados cadastrais. O tipo de perfil não pode ser alterado por você.
        </CardDescription>
        <div className="pt-1">
          <Badge variant="secondary" className="text-xs">
            {ROLE_LABELS[perfil.role as UserRole] ?? perfil.role}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="perfil-nome">Nome completo *</Label>
            <Input
              id="perfil-nome"
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              placeholder="Seu nome"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="perfil-telefone">Telefone *</Label>
            <Input
              id="perfil-telefone"
              value={formatTelefone(form.telefone)}
              onChange={(e) =>
                setForm((f) => ({ ...f, telefone: e.target.value.replace(/\D/g, "") }))
              }
              placeholder="(00) 00000-0000"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="perfil-estado">Estado *</Label>
              <Select
                value={form.estado || ""}
                onValueChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    estado: v,
                    cidade: f.estado === v ? f.cidade : "",
                  }))
                }
                disabled={estadosLoading}
              >
                <SelectTrigger id="perfil-estado">
                  <SelectValue placeholder={estadosLoading ? "Carregando..." : "Selecione..."} />
                </SelectTrigger>
                <SelectContent>
                  {estados.map((e) => (
                    <SelectItem key={e.sigla} value={e.sigla}>
                      {e.sigla} - {e.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="perfil-cidade">Cidade *</Label>
              <Select
                value={form.cidade || ""}
                onValueChange={(v) => setForm((f) => ({ ...f, cidade: v }))}
                disabled={!form.estado || cidadesLoading}
              >
                <SelectTrigger id="perfil-cidade">
                  <SelectValue
                    placeholder={
                      !form.estado
                        ? "Selecione o estado primeiro"
                        : cidadesLoading
                          ? "Carregando..."
                          : "Selecione a cidade"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {cidades.map((c) => (
                    <SelectItem key={c.id} value={c.municipio}>
                      {c.municipio}
                    </SelectItem>
                  ))}
                  {form.cidade &&
                    !cidadesLoading &&
                    !cidades.some((c) => c.municipio === form.cidade) && (
                    <SelectItem key="loaded-cidade" value={form.cidade}>
                      {form.cidade}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="perfil-cpf">CPF</Label>
            <Input
              id="perfil-cpf"
              value={formatCPF(form.cpf)}
              onChange={(e) =>
                setForm((f) => ({ ...f, cpf: e.target.value.replace(/\D/g, "") }))
              }
              placeholder="000.000.000-00"
            />
          </div>

          {mostrarPix && (
            <div className="space-y-2">
              <Label htmlFor="perfil-pix">Chave PIX</Label>
              <Input
                id="perfil-pix"
                value={form.pix}
                onChange={(e) => setForm((f) => ({ ...f, pix: e.target.value }))}
                placeholder="CPF, e-mail, telefone ou chave aleatória"
              />
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            O e-mail de acesso é {user?.email}. Alterações de e-mail devem ser feitas pelo
            administrador.
          </p>

          <Button type="submit" disabled={saving}>
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Salvar alterações
          </Button>
        </form>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5" />
          Alterar senha de acesso
        </CardTitle>
        <CardDescription>
          Use as mesmas regras de criação de senha do sistema (mínimo 6 caracteres, letras e números).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleAlterarSenha} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="perfil-senha-nova">Nova senha *</Label>
            <div className="relative">
              <Input
                id="perfil-senha-nova"
                type={mostrarSenha ? "text" : "password"}
                value={senhaNova}
                onChange={(e) => setSenhaNova(e.target.value)}
                placeholder="Digite a nova senha"
                disabled={salvandoSenha}
                className="pr-10"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setMostrarSenha(!mostrarSenha)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {mostrarSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="perfil-senha-confirmar">Confirmar nova senha *</Label>
            <div className="relative">
              <Input
                id="perfil-senha-confirmar"
                type={mostrarConfirmar ? "text" : "password"}
                value={senhaConfirmar}
                onChange={(e) => setSenhaConfirmar(e.target.value)}
                placeholder="Digite a senha novamente"
                disabled={salvandoSenha}
                className="pr-10"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setMostrarConfirmar(!mostrarConfirmar)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {mostrarConfirmar ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
            <p className="text-sm font-medium">Requisitos da senha:</p>
            <div className="space-y-1">
              <ValidacaoItem valido={validacoesSenha.tamanho} texto="Mínimo 6 caracteres" />
              <ValidacaoItem valido={validacoesSenha.temLetra} texto="Pelo menos uma letra" />
              <ValidacaoItem valido={validacoesSenha.temNumero} texto="Pelo menos um número" />
              <ValidacaoItem valido={validacoesSenha.senhasIguais} texto="Senhas coincidem" />
            </div>
          </div>
          <Button type="submit" variant="outline" disabled={salvandoSenha || !senhaValida}>
            {salvandoSenha ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Lock className="mr-2 h-4 w-4" />
            )}
            Alterar senha
          </Button>
        </form>
      </CardContent>
    </Card>
    </div>
  );
}
